import StatusCode from 'status-code-enum';
import getConfig from 'next/config';
import { isAxiosError } from 'axios';

import { adViewApi } from '../../../../api/adViewApi';
import { fireToast } from '../../../../helpers/Toasts';
import { ShowPhoneNumberButton } from '../../../../components/Toolkit/ShowPhoneNumberButton/ShowPhoneNumberButton';
import { pushToDataLayer } from '../../../../services/gtmService';

import { useScript } from '../../../../hooks/UseScript';
import { getRecaptchaToken } from '../../../../helpers/reCaptcha';
import { useBraze } from '../../../../hooks/useBraze';
import { useAuthOptions } from '../../../../hooks/useAuthOptions';
import type { SellerTypes } from '../../../../components/PageWrappers/AdDetails/types';
import { useLeadTracking } from '../../../../hooks/useConvertLeadTracking';
import { PAGE } from '../../../../helpers/pages';
import { CONVERT_GOAL_KEYS } from '../../../../helpers/convertGoalKeys';

import { logger } from '@dist-marketplace-frontend/services';

const {
  publicRuntimeConfig: { GOOGLE_RECAPTCHA, INTEGRATION_TESTING },
} = getConfig();

interface RevealPhoneNumberProps {
  adId: number;
  showNumberTextVariant?: boolean;
  size?: 'SMALL' | 'LARGE' | 'GROW';
  trackingId?: string;
  clickSource?: string;
  isPrimaryButton?: boolean;
  campaignId?: string | null;
  disabled?: boolean;
  sectionName?: string;
  sellerType?: SellerTypes;
}

function RevealPhoneNumber({
  adId,
  showNumberTextVariant = false,
  size = 'SMALL',
  trackingId = 'reveal-number',
  clickSource = '',
  isPrimaryButton = false,
  campaignId,
  disabled,
  sectionName,
  sellerType,
}: RevealPhoneNumberProps) {
  useScript(
    `https://www.google.com/recaptcha/enterprise.js?render=${GOOGLE_RECAPTCHA}`,
    'recaptcha',
    [adId],
  );

  const { handleLogin } = useAuthOptions();
  const { logBrazeRevealPhoneEvent } = useBraze();
  const { trackLead } = useLeadTracking({ page: PAGE.AD_DETAILS });

  const shouldTrackLead = sellerType === 'DEALER' && sectionName === 'cars';

  async function handleReveal() {
    try {
      const captchaToken = await getRecaptchaToken(
        'phone_reveal',
        GOOGLE_RECAPTCHA,
        INTEGRATION_TESTING,
      );
      const { data } = await adViewApi.getPhoneNumber(
        adId,
        captchaToken,
        campaignId,
      );

      pushToDataLayer({ event: 'phone_reveal', source: clickSource });
      logBrazeRevealPhoneEvent(sectionName, sellerType);
      if (shouldTrackLead) {
        trackLead(CONVERT_GOAL_KEYS.PRO_LEAD_INTENTION);
      }
      return data.phoneNumber;
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === StatusCode.ClientErrorUnauthorized
      ) {
        handleLogin();
      } else {
        logger.error({
          error,
          tags: [PAGE.AD_DETAILS],
          message: 'Error revealing phone number',
        });
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
      return false;
    }
  }

  async function handleOnCall() {
    try {
      pushToDataLayer({ event: 'click_to_call', source: clickSource });
      if (shouldTrackLead) {
        trackLead(CONVERT_GOAL_KEYS.PRO_LEAD_GENERATED);
      }
      await adViewApi.trackAdCallNumber(adId, campaignId);
    } catch (error) {
      logger.error({
        error,
        message: 'Error tracking call to phone number',
        tags: [PAGE.AD_DETAILS],
      });
    }
  }

  return (
    <ShowPhoneNumberButton
      adId={adId}
      onReveal={handleReveal}
      showNumberTextVariant={showNumberTextVariant}
      size={size}
      trackingId={trackingId}
      onCall={handleOnCall}
      isPrimaryButton={isPrimaryButton}
      disabled={disabled}
    />
  );
}

export { RevealPhoneNumber, type RevealPhoneNumberProps };
